/*
 * The app sidebar: logo, primary nav in two groups, help links and the collapse toggle. Collapsed it keeps
 * icons with tooltips (remembered per device). Below `lg` the same nav lives in a start-side drawer.
 */
import { BookOpen, CaretDoubleRight, Pulse } from '@phosphor-icons/react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, NavLink } from 'react-router'
import { SoonBadge, Tooltip } from '@/components/ui'
import { cn } from '@/lib/cn'
import { isMock } from '@/lib/env'
import { isPlaceholder, siteLinks } from '@/lib/site'
import { useLocale } from '@/i18n/useLocale'
import { LogoLockup, LogoMark } from './Logo'
import { NAV, type NavItem } from './nav'

function NavRow({
  item,
  collapsed,
  onNavigate,
}: {
  item: NavItem
  collapsed?: boolean
  onNavigate?: () => void
}) {
  const { t } = useTranslation('shell')
  const { isRtl } = useLocale()
  const label = t(`nav.${item.id}`)
  const link = (
    <NavLink
      to={item.to}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'group flex h-9 items-center gap-3 rounded-xl px-2.5 text-14 transition-[background-color,color,box-shadow] duration-150 touch:min-h-11',
          collapsed && 'justify-center px-0',
          isActive
            ? 'bg-surface font-semibold text-ink shadow-sm ring-1 ring-hairline'
            : 'text-muted hover:bg-control hover:text-ink',
        )
      }
    >
      {({ isActive }) => (
        <>
          <item.icon
            className={cn(
              'size-[19px] shrink-0 transition-colors',
              isActive ? 'text-brand-600' : 'text-subtle group-hover:text-body',
            )}
            weight={isActive ? 'fill' : 'regular'}
            aria-hidden="true"
          />
          <span className={cn('truncate', collapsed && 'sr-only')}>{label}</span>
        </>
      )}
    </NavLink>
  )
  return collapsed ? (
    <Tooltip content={label} side={isRtl ? 'left' : 'right'}>
      {link}
    </Tooltip>
  ) : (
    link
  )
}

export function NavList({ collapsed, onNavigate }: { collapsed?: boolean; onNavigate?: () => void }) {
  const { t } = useTranslation('shell')
  const groups = (['main', 'account'] as const).map((g) => ({
    id: g,
    items: NAV.filter((n) => n.group === g),
  }))
  return (
    <nav aria-label={t('a11y.mainNav')} className="flex flex-col gap-5">
      {groups.map((g) => (
        <div key={g.id} className="flex flex-col gap-0.5">
          {g.id === 'account' ? (
            collapsed ? (
              <span aria-hidden="true" className="mx-3 mb-2 h-px bg-hairline" />
            ) : (
              <span className="px-2.5 pb-1.5 text-12 font-semibold text-subtle">
                {t('nav.groups.account')}
              </span>
            )
          ) : null}
          {g.items.map((item) => (
            <NavRow key={item.id} item={item} collapsed={collapsed} onNavigate={onNavigate} />
          ))}
        </div>
      ))}
    </nav>
  )
}

function FooterLink({
  href,
  icon,
  label,
  collapsed,
  soon,
}: {
  href: string
  icon: ReactNode
  label: string
  collapsed?: boolean
  /** a placeholder page (src/lib/site.ts) */
  soon?: boolean
}) {
  const { t } = useTranslation('common')
  const { isRtl } = useLocale()
  const a = (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className={cn(
        'flex h-8 items-center gap-3 rounded-lg px-2.5 text-13 text-muted transition-colors hover:bg-control hover:text-ink touch:min-h-11 [&_svg]:size-[17px] [&_svg]:text-subtle',
        collapsed && 'justify-center px-0',
      )}
    >
      {icon}
      <span className={cn(collapsed && 'sr-only')}>{label}</span>
      {soon && !collapsed ? <SoonBadge className="ms-auto" /> : null}
      {soon && collapsed ? <span className="sr-only">{t('placeholder.soon')}</span> : null}
    </a>
  )
  return collapsed ? (
    <Tooltip content={soon ? `${label} · ${t('placeholder.soon')}` : label} side={isRtl ? 'left' : 'right'}>
      {a}
    </Tooltip>
  ) : (
    a
  )
}

export function SidebarFooter({ collapsed }: { collapsed?: boolean }) {
  const { t } = useTranslation('shell')
  const { locale } = useLocale()
  return (
    <div className="flex flex-col gap-0.5">
      <FooterLink
        href={siteLinks.docs(locale)}
        icon={<BookOpen />}
        label={t('help.docs')}
        collapsed={collapsed}
        soon={isPlaceholder('docs')}
      />
      <FooterLink
        href={siteLinks.status()}
        icon={<Pulse />}
        label={t('help.status')}
        collapsed={collapsed}
        soon={isPlaceholder('status')}
      />
      {isMock && !collapsed ? (
        <span className="mt-2 inline-flex w-fit items-center gap-1.5 rounded-full bg-warm-50 px-2.5 py-1 text-12 font-semibold text-warm-700 ring-1 ring-warm-200 ring-inset">
          <span className="size-1.5 rounded-full bg-warm-500" aria-hidden="true" />
          {t('mock.badge')}
        </span>
      ) : null}
    </div>
  )
}

export function Sidebar({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  const { t } = useTranslation('shell')
  return (
    <aside
      className={cn(
        'sticky top-0 scrollbar-none hidden h-dvh shrink-0 flex-col gap-6 overflow-y-auto px-3 pt-4 pb-4 transition-[width] duration-200 lg:flex print:hidden',
        collapsed ? 'w-[76px]' : 'w-[248px]',
      )}
    >
      <div className={cn('flex h-10 items-center', collapsed ? 'justify-center' : 'px-2.5')}>
        <Link to="/overview" aria-label={t('a11y.home')} className="rounded-lg">
          {collapsed ? <LogoMark className="h-7 w-auto" /> : <LogoLockup className="h-[22px] w-auto" />}
        </Link>
      </div>
      <div className="flex-1">
        <NavList collapsed={collapsed} />
      </div>
      <SidebarFooter collapsed={collapsed} />
      <button
        type="button"
        onClick={onToggle}
        aria-label={collapsed ? t('a11y.expandSidebar') : t('a11y.collapseSidebar')}
        aria-expanded={!collapsed}
        className={cn(
          'flex h-8 items-center gap-3 rounded-lg px-2.5 text-13 text-muted transition-colors hover:bg-control hover:text-ink',
          collapsed && 'justify-center px-0',
        )}
      >
        <CaretDoubleRight
          className={cn(
            'size-4 text-subtle transition-transform',
            collapsed ? 'rotate-180 flip-ltr' : 'flip-ltr',
          )}
          aria-hidden="true"
        />
        <span className={cn(collapsed && 'sr-only')}>{t('a11y.collapseSidebar')}</span>
      </button>
    </aside>
  )
}
